import { useState } from 'react';
import { Stack, Card, Text, Button, Badge, Group, SimpleGrid } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, DataTable, MetricCard } from '@repletoryreach/sdk-ui/data-display';
import { useAffiliate, useAffiliateEarnings } from '../../../data/useAffiliate';
import { formatMinor, formatDate, formatDateTime } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import '../refer.css';

/**
 * Every cent, and where it came from.
 *
 * One row per company per day, which is how the platform works it out: the
 * day's usage is added up and the share taken once, rather than a fraction of
 * a cent per message. A day that nets to refunds shows as a negative row, and
 * the pair reads as one correction.
 */
export default function ReferEarningsPage() {
  const t = useT();
  const me = useAffiliate();
  const [kind, setKind] = useState<string | undefined>(undefined);
  const earnings = useAffiliateEarnings(kind);

  if (!me.isLoading && !me.data) {
    return (
      <Stack gap="lg">
        <PageHeader eyebrow={t('refer.eyebrow')} title={t('refer.earnings.title')} />
        <EmptyState title={t('refer.notEnrolled.title')} description={t('refer.notEnrolled.body')} />
      </Stack>
    );
  }

  const rows = (earnings.data?.data ?? []).map((e, i) => ({ ...e, _i: i }));
  const meta = earnings.data?.meta;
  const kinds: { value?: string; labelKey: string }[] = [
    { value: undefined, labelKey: 'refer.filter.all' },
    { value: 'commission', labelKey: 'refer.kind.commission' },
    { value: 'adjustment', labelKey: 'refer.kind.adjustment' },
    { value: 'withdrawal', labelKey: 'refer.kind.withdrawal' }
  ];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('refer.eyebrow')}
        title={t('refer.earnings.title')}
        description={t('refer.earnings.description')}
      />

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <MetricCard label={t('refer.metric.available')} value={formatMinor(meta?.available_minor ?? me.data?.balances.available_minor ?? 0)} />
        <MetricCard label={t('refer.metric.pending')} value={formatMinor(meta?.pending_minor ?? me.data?.balances.pending_minor ?? 0)} />
      </SimpleGrid>

      <Group gap="sm">
        {kinds.map((k) => (
          <Button key={k.labelKey} size="sm" variant={kind === k.value ? 'filled' : 'light'} onClick={() => setKind(k.value)}>
            {t(k.labelKey)}
          </Button>
        ))}
      </Group>

      <Card withBorder>
        {earnings.isLoading ? (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
        ) : earnings.isError ? (
          <Alert color="error" role="alert">
            {t('refer.earnings.loadFailed')}{' '}
            <Button variant="subtle" size="sm" onClick={() => void earnings.refetch()}>{t('action.retry')}</Button>
          </Alert>
        ) : rows.length === 0 ? (
          <EmptyState
            title={kind ? t('refer.earnings.noneMatch') : t('refer.earnings.emptyTitle')}
            description={kind ? t('refer.earnings.noneMatchBody') : t('refer.earnings.emptyBody')}
          />
        ) : (
          <DataTable<Indexed<(typeof rows)[number]>>
            idAccessor="_i"
            records={rows}
            columns={[
              { accessor: 'usage_date', title: t('refer.column.day'), render: (r) => r.usage_date ? formatDate(r.usage_date) : formatDateTime(r.occurred_at) },
              { accessor: 'referred_name', title: t('refer.column.company'), render: (r) => r.referred_name ?? '—' },
              { accessor: 'kind', title: t('refer.column.kind'), render: (r) => <Badge>{t(`refer.kind.${r.kind}`, r.kind)}</Badge> },
              { accessor: 'description', title: t('refer.column.detail'), render: (r) => <Text role="bodySmall">{r.description}</Text> },
              { accessor: 'amount_minor', title: t('refer.column.amount'), render: (r) => <span className="rr-refer-amount">{formatMinor(r.amount_minor)}</span> },
              { accessor: 'cleared', title: t('refer.column.clears'), render: (r) => r.cleared ? t('refer.cleared') : formatDate(r.available_at) }
            ]}
          />
        )}
      </Card>
    </Stack>
  );
}
